import { useState, ReactNode } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Plus, MoreVertical } from 'lucide-react'
import { api } from '../lib/api'
import AdminLayout from '../components/AdminLayout'
import FilesTab from '../components/FilesTab'
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  extractErr,
  Dash,
  InlineText,
  InlineSelect,
  InlineStatus,
  RecordDrawer,
  useDeleteConfirm,
} from '@/components/ui/inline-edit'

// ─────────────────── Types ───────────────────
type Department = {
  id: number
  code: string
  name: string
  description: string
  parent: number | null
  parent_name: string | null
  manager: number | null
  manager_name: string | null
  manager_person: number | null
  manager_person_name: string | null
  is_billable_to_client: boolean
  is_active: boolean
  employee_count: number
}

type Person = {
  id: number
  full_name: string
}

type Employment = {
  id: number
  person: number
  person_name: string
  employee_number: string
  job_title: string
  department: number | null
  status: 'PRE_HIRE' | 'ACTIVE' | 'ON_LEAVE' | 'ENDED'
  employment_type: 'FULL_TIME' | 'PART_TIME' | 'CONTRACTOR' | 'INTERN'
}

// ─────────────────── Constants ───────────────────
const ACTIVE_COLOR: Record<'ACTIVE' | 'INACTIVE', string> = {
  ACTIVE: 'bg-green-100 text-green-800',
  INACTIVE: 'bg-gray-100 text-gray-600',
}

const BILLABLE_COLOR: Record<'BILLABLE' | 'INTERNAL', string> = {
  BILLABLE: 'bg-blue-100 text-blue-800',
  INTERNAL: 'bg-gray-100 text-gray-700',
}

const EMP_STATUS_COLOR: Record<Employment['status'], string> = {
  PRE_HIRE: 'bg-gray-100 text-gray-700',
  ACTIVE: 'bg-green-100 text-green-800',
  ON_LEAVE: 'bg-amber-100 text-amber-800',
  ENDED: 'bg-slate-100 text-slate-700',
}

type TabKey = 'details' | 'subs' | 'employees' | 'files'

// ─────────────────── Page ───────────────────
export default function HRDepartmentDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const deptId = id ? Number(id) : 0
  const { confirm, dialog: deleteDialog } = useDeleteConfirm()

  const deptQ = useQuery({
    queryKey: ['hr', 'department', deptId],
    queryFn: async () => (await api.get<Department>(`/hr/departments/${deptId}/`)).data,
    enabled: !!deptId,
  })
  const departmentsQ = useQuery({
    queryKey: ['hr', 'departments'],
    queryFn: async () => (await api.get<Department[]>('/hr/departments/')).data,
  })
  const personsQ = useQuery({
    queryKey: ['hr', 'persons', 'manager-candidates'],
    queryFn: async () =>
      (await api.get<Person[]>('/hr/persons/?manager_candidates=1')).data,
  })
  const employmentsQ = useQuery({
    queryKey: ['hr', 'employments'],
    queryFn: async () => (await api.get<Employment[]>('/hr/employments/')).data,
  })

  const updateMut = useMutation({
    mutationFn: async (payload: Partial<Department>) =>
      (await api.patch(`/hr/departments/${deptId}/`, payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['hr', 'department', deptId] })
      qc.invalidateQueries({ queryKey: ['hr', 'departments'] })
    },
  })

  const deleteMut = useMutation({
    mutationFn: async () => api.delete(`/hr/departments/${deptId}/`),
    onSuccess: () => {
      toast.success('Department deleted')
      qc.invalidateQueries({ queryKey: ['hr', 'departments'] })
      navigate('/admin/hr/departments')
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  const [tab, setTab] = useState<TabKey>('details')

  if (deptQ.isLoading) {
    return <AdminLayout><p className="text-muted-foreground">Loading…</p></AdminLayout>
  }
  if (deptQ.isError || !deptQ.data) {
    return (
      <AdminLayout>
        <div className="bg-card rounded-lg shadow p-8 text-center">
          <p className="text-foreground mb-4">Department not found.</p>
          <Link to="/admin/hr/departments" className="text-blue-600 hover:underline">← Back to departments</Link>
        </div>
      </AdminLayout>
    )
  }

  const dept = deptQ.data
  const allDepts = departmentsQ.data || []
  const allPersons = personsQ.data || []
  const allEmployments = employmentsQ.data || []

  // Sub-departments = active and inactive that have parent = this dept
  const subDepts = allDepts.filter((d) => d.parent === dept.id)
  // Employments in this department, ordered by status (active first)
  const deptEmployments = allEmployments.filter((e) => e.department === dept.id)

  // Parent options: any active department except this one and its descendants
  // (to avoid circular references). Build a descendant set.
  const descendantIds = (() => {
    const set = new Set<number>([dept.id])
    let changed = true
    while (changed) {
      changed = false
      for (const d of allDepts) {
        if (d.parent !== null && set.has(d.parent) && !set.has(d.id)) {
          set.add(d.id); changed = true
        }
      }
    }
    return set
  })()
  const parentOptions = allDepts.filter((d) => d.is_active && !descendantIds.has(d.id))

  const canDelete = dept.employee_count === 0 && subDepts.length === 0

  const save = (payload: Partial<Department>) =>
    updateMut.mutateAsync(payload).then(() => undefined)

  return (
    <AdminLayout>
      {deleteDialog}

      {/* Header */}
      <div className="mb-4">
        <div className="text-xs">
          <Link to="/admin/hr/departments" className="text-muted-foreground hover:text-foreground hover:underline">← Departments</Link>
        </div>
        <div className="flex items-start justify-between mt-2">
          <div className="flex-1 min-w-0">
            <div className="font-mono text-xs text-muted-foreground">{dept.code}</div>
            <h2 className="text-2xl font-bold text-foreground">
              <InlineText value={dept.name} onSave={(v) => save({ name: v })} />
            </h2>
            <div className="text-sm text-muted-foreground">
              {dept.parent_name ? (
                <Link to={`/admin/hr/departments/${dept.parent}`} className="hover:underline">
                  ↳ under {dept.parent_name}
                </Link>
              ) : (
                <span className="italic">top-level</span>
              )}
              {dept.manager_name && <span> · Managed by {dept.manager_name}</span>}
              <span> · {dept.employee_count} employee{dept.employee_count === 1 ? '' : 's'}</span>
              {subDepts.length > 0 && (
                <span> · {subDepts.length} sub-department{subDepts.length === 1 ? '' : 's'}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <InlineStatus
              value={dept.is_billable_to_client ? 'BILLABLE' : 'INTERNAL'}
              colorMap={BILLABLE_COLOR}
              options={[
                { value: 'BILLABLE', label: 'Billable' },
                { value: 'INTERNAL', label: 'Internal' },
              ]}
              onSave={(v) => save({ is_billable_to_client: v === 'BILLABLE' })}
            />
            <InlineStatus
              value={dept.is_active ? 'ACTIVE' : 'INACTIVE'}
              colorMap={ACTIVE_COLOR}
              options={[
                { value: 'ACTIVE', label: 'Active' },
                { value: 'INACTIVE', label: 'Inactive' },
              ]}
              onSave={(v) => save({ is_active: v === 'ACTIVE' })}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button type="button" className="p-1 rounded hover:bg-muted text-muted-foreground">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  disabled={!canDelete}
                  onClick={() => canDelete && confirm(
                    `department ${dept.code} (${dept.name})`,
                    () => deleteMut.mutate(),
                  )}
                  className={canDelete ? 'text-destructive' : ''}
                >
                  {canDelete
                    ? 'Delete department'
                    : dept.employee_count > 0
                      ? `Delete (${dept.employee_count} employees)`
                      : `Delete (${subDepts.length} sub-departments)`}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      {/* Tab bar */}
      <div className="border-b mb-6">
        <nav className="flex gap-1 text-sm">
          {(['details', 'subs', 'employees', 'files'] as TabKey[]).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 -mb-px border-b-2 ${
                tab === t ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t === 'details' && 'Details'}
              {t === 'subs' && `Sub-departments (${subDepts.length})`}
              {t === 'employees' && `Employees (${deptEmployments.length})`}
              {t === 'files' && 'Files'}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'details' && (
        <DetailsTab
          dept={dept}
          parentOptions={parentOptions}
          persons={allPersons}
          onSave={save}
        />
      )}
      {tab === 'subs' && (
        <SubsTab dept={dept} subDepts={subDepts} />
      )}
      {tab === 'employees' && (
        <EmployeesTab employments={deptEmployments} />
      )}
      {tab === 'files' && <FilesTab parent="hr.department" parentId={deptId} />}
    </AdminLayout>
  )
}

// ─────────────────── Details tab ───────────────────

function DetailsTab({
  dept, parentOptions, persons, onSave,
}: {
  dept: Department
  parentOptions: Department[]
  persons: Person[]
  onSave: (payload: Partial<Department>) => Promise<void>
}) {
  return (
    <div className="space-y-6 max-w-3xl">
      <Section title="Identity">
        <Row label="Code">
          <InlineText
            value={dept.code}
            onSave={(v) => onSave({ code: v })}
            className="font-mono"
          />
        </Row>
        <Row label="Name">
          <InlineText value={dept.name} onSave={(v) => onSave({ name: v })} />
        </Row>
        <Row label="Description">
          <InlineText
            value={dept.description}
            multiline
            onSave={(v) => onSave({ description: v })}
            placeholder="What this department does"
          />
        </Row>
      </Section>

      <Section title="Hierarchy">
        <Row label="Parent department">
          <InlineSelect
            value={dept.parent ? String(dept.parent) : ''}
            displayLabel={dept.parent_name || <span className="italic text-muted-foreground">top-level</span>}
            allowClear
            options={parentOptions.map((d) => ({
              value: String(d.id),
              label: `${d.code} ${d.name}`,
              searchText: `${d.code} ${d.name}`,
            }))}
            onSave={(v) => onSave({ parent: v ? Number(v) : null })}
            helper="Leave empty for a top-level department. Descendants are excluded to prevent cycles."
          />
        </Row>
        <Row label="Manager">
          <InlineSelect
            value={dept.manager_person ? String(dept.manager_person) : ''}
            displayLabel={dept.manager_person_name || dept.manager_name || <Dash />}
            allowClear
            options={persons.map((p) => ({
              value: String(p.id),
              label: p.full_name,
              searchText: p.full_name,
            }))}
            onSave={(v) => onSave({ manager_person: v ? Number(v) : null })}
            helper="People manager for this department. Drives the HR visibility scope."
          />
        </Row>
      </Section>

      <Section title="Behavior">
        <Row label="Billable to client">
          <InlineStatus
            value={dept.is_billable_to_client ? 'BILLABLE' : 'INTERNAL'}
            colorMap={BILLABLE_COLOR}
            options={[
              { value: 'BILLABLE', label: 'Billable' },
              { value: 'INTERNAL', label: 'Internal' },
            ]}
            onSave={(v) => onSave({ is_billable_to_client: v === 'BILLABLE' })}
          />
        </Row>
        <Row label="Status">
          <InlineStatus
            value={dept.is_active ? 'ACTIVE' : 'INACTIVE'}
            colorMap={ACTIVE_COLOR}
            options={[
              { value: 'ACTIVE', label: 'Active' },
              { value: 'INACTIVE', label: 'Inactive' },
            ]}
            onSave={(v) => onSave({ is_active: v === 'ACTIVE' })}
          />
        </Row>
      </Section>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="bg-card rounded-lg shadow p-6">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-3">{title}</h3>
      <dl className="grid grid-cols-[180px_1fr] gap-x-6 gap-y-4 text-sm">
        {children}
      </dl>
    </section>
  )
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pt-1">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}

// ─────────────────── Sub-departments tab ───────────────────

function SubsTab({ dept, subDepts }: { dept: Department; subDepts: Department[] }) {
  const qc = useQueryClient()
  const navigate = useNavigate()

  const [creating, setCreating] = useState(false)
  const [draft, setDraft] = useState({
    name: '',
    description: '',
    is_billable_to_client: dept.is_billable_to_client,
  })

  function openCreate() {
    setDraft({
      name: '', description: '',
      is_billable_to_client: dept.is_billable_to_client,
    })
    setCreating(true)
  }

  const createMut = useMutation({
    mutationFn: async () => (await api.post<Department>('/hr/departments/', {
      ...draft,
      parent: dept.id,
      manager_person: null,
      is_active: true,
    })).data,
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['hr', 'departments'] })
      toast.success('Sub-department added')
      setCreating(false)
      navigate(`/admin/hr/departments/${created.id}`)
    },
    onError: (e) => toast.error(extractErr(e)),
  })

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <p className="text-xs text-muted-foreground max-w-2xl">
          Direct children of this department. Open a sub-department to edit its details or to drill further down. Inherits "billable" by default from the parent.
        </p>
        <Button size="sm" onClick={openCreate}><Plus className="h-3 w-3 mr-1" /> Add sub-department</Button>
      </div>

      {subDepts.length === 0 ? (
        <p className="text-muted-foreground text-sm">No sub-departments under {dept.name}.</p>
      ) : (
        <div className="bg-card rounded border overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left">
              <tr>
                <Th>Code</Th>
                <Th>Name</Th>
                <Th>Manager</Th>
                <Th className="text-right">Employees</Th>
                <Th>Billable</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {subDepts.map((d) => (
                <tr
                  key={d.id}
                  className="border-t border-border hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/admin/hr/departments/${d.id}`)}
                >
                  <Td className="font-mono text-xs">{d.code}</Td>
                  <Td className="font-medium">{d.name}</Td>
                  <Td className="text-xs">{d.manager_name || <Dash />}</Td>
                  <Td className="text-right text-xs">{d.employee_count}</Td>
                  <Td className="text-xs">
                    {d.is_billable_to_client
                      ? <span className="text-blue-700">Billable</span>
                      : <span className="text-muted-foreground">Internal</span>}
                  </Td>
                  <Td className="text-xs">
                    {d.is_active
                      ? <span className="text-green-700">Active</span>
                      : <span className="text-muted-foreground">Inactive</span>}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <RecordDrawer
        open={creating}
        onOpenChange={(v) => { if (!v) setCreating(false) }}
        title={`Add sub-department under ${dept.name}`}
        description="The parent is pre-set. Manager and other details can be filled in on the new record page."
        onSubmit={() => createMut.mutate()}
        isPending={createMut.isPending}
        submitLabel="Create"
      >
        <FieldGroup>
          <Field>
            <FieldLabel htmlFor="sd-name">Name *</FieldLabel>
            <Input id="sd-name" autoFocus required value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
          </Field>
          <Field>
            <FieldLabel htmlFor="sd-desc">Description</FieldLabel>
            <Textarea id="sd-desc" rows={2} value={draft.description}
              onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          </Field>
          <Field>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={draft.is_billable_to_client}
                onChange={(e) => setDraft({ ...draft, is_billable_to_client: e.target.checked })} />
              Billable to client
            </label>
            <FieldDescription>Defaults to the parent's billable flag.</FieldDescription>
          </Field>
        </FieldGroup>
      </RecordDrawer>
    </div>
  )
}

// ─────────────────── Employees tab ───────────────────
// Read-only. Add/edit happens on the employee record page.

function EmployeesTab({ employments }: { employments: Employment[] }) {
  const navigate = useNavigate()

  if (employments.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">No employments in this department.</p>
    )
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground max-w-2xl">
        Employments assigned to this department. Click a row to open the Employment record page.
      </p>
      <div className="bg-card rounded border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left">
            <tr>
              <Th>#</Th>
              <Th>Name</Th>
              <Th>Job title</Th>
              <Th>Type</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {employments.map((e) => (
              <tr
                key={e.id}
                className="border-t border-border hover:bg-muted/40 cursor-pointer"
                onClick={() => navigate(`/admin/hr/employments/${e.id}`)}
              >
                <Td className="font-mono text-xs">{e.employee_number}</Td>
                <Td className="font-medium">{e.person_name}</Td>
                <Td className="text-xs">{e.job_title || <Dash />}</Td>
                <Td className="text-xs">{e.employment_type}</Td>
                <Td>
                  <span className={`text-xs px-2 py-0.5 rounded font-semibold ${EMP_STATUS_COLOR[e.status]}`}>
                    {e.status.replace('_', ' ')}
                  </span>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ─────────────────── Helpers ───────────────────
function Th({ children, className = '' }: { children?: ReactNode; className?: string }) {
  return <th className={`px-4 py-2 text-left text-xs font-semibold text-muted-foreground uppercase ${className}`}>{children}</th>
}
function Td({ children, className = '', onClick }:
  { children?: ReactNode; className?: string; onClick?: (e: React.MouseEvent) => void }) {
  return <td className={`px-4 py-2 text-foreground ${className}`} onClick={onClick}>{children}</td>
}
